import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { Rnd } from 'react-rnd';
import { CircularProgress } from '@material-ui/core';
import MicOffIcon from '@material-ui/icons/MicOff';
import { isMobile } from 'react-device-detect';

import FirebaseStore from 'stores/FirebaseStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import Cam2CamStore from 'stores/Cam2CamStore';
import CornerDrag from 'shared/icons/cornerDrag';

import useCam2Cam, { DEFAULT_ASPECT_RATIO } from './useCam2Cam';
import styles from './index.css';


const desktopMinWidth = 220;
const minWidth = isMobile ? 100 : desktopMinWidth;
const wrapperStyle = {
  zIndex: '100',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 0 25px rgba(0,0,0,0.3)',
  background: '#f0f0f0',
  borderRadius: '2px',
};
const enableResizing = { bottomRight: true };
const resizeHandleClasses = { bottomRight: styles.resizeHandler };
const resizeHandleComponent = { bottomRight: <CornerDrag className={styles.resizeHandler} /> };


const Cam2CamBroadcast = ({ previewOnly }) => {
  const { user: { sfw } } = UserStore;
  const { onRightClick, aspectRatio } = useCam2Cam(UserStore, ProfileStore, FirebaseStore, previewOnly);
  const videoClass = classNames({ [styles.blur]: sfw });
  const [size, setSize] = useState({ width: minWidth, height: minWidth / DEFAULT_ASPECT_RATIO });

  const { c2cAudioMuted, c2cVideoMuted } = UserStore;
  const { c2cPreviewStream, setC2cPreviewStream } = Cam2CamStore;

  const draggerClassnames = [{ [styles.hidden]: c2cVideoMuted }];

  useEffect(() => {
    if (c2cPreviewStream) {
      document.getElementById('c2c-input-preview').srcObject = c2cPreviewStream;
    }

    return () => setC2cPreviewStream(null);
  }, [c2cPreviewStream, setC2cPreviewStream]);

  useEffect(() => {
    setSize({ width: size.width, height: size.width / aspectRatio });
  }, [aspectRatio, size.width]);

  const onResize = useCallback((_, __, ref) => {
    setSize({
      width: ref.offsetWidth,
      height: ref.offsetHeight,
    });
  }, []);

  const previewIndicator = useMemo(() => {
    if (!previewOnly) {
      return null;
    }

    return (
      <div className={styles.overlay}>
        <div className={styles.loading}>
          Request Pending
        </div>
      </div>
    );
  }, [previewOnly]);

  return (
    <Rnd
      bounds="#app"
      className={draggerClassnames}
      dragHandleClassName={styles.peekWrapper}
      enableResizing={enableResizing}
      lockAspectRatio={aspectRatio}
      minWidth={`${minWidth}px`}
      resizeHandleClasses={resizeHandleClasses}
      resizeHandleComponent={resizeHandleComponent}
      size={size}
      style={wrapperStyle}
      onResize={onResize}
    >
      <div className={styles.peekWrapper}>
        <CircularProgress color="white" />
        {previewIndicator}
        <video
          autoPlay={true}
          className={videoClass}
          controlsList="nodownload"
          disablePictureInPicture={true}
          id="c2c-input-preview"
          muted={true}
          playsInline={true}
          onContextMenu={onRightClick}
        />
        {c2cAudioMuted && (
          <div className={styles.muteIconWrapper}>
            <MicOffIcon className={styles.muteIcon} />
          </div>
        )}
      </div>
    </Rnd>
  );
};

Cam2CamBroadcast.propTypes = { previewOnly: PropTypes.bool.isRequired };

export default observer(Cam2CamBroadcast);
